<template>
  <div
    ref="selectWrapper"
    v-click-outside:[popperPaneRef]="handleClose"
    :class="[
      namespace + '-input-select',
      selectSize,
      block ? 'block' : ''
    ]"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click.stop="toggleMenu"
  >
    <hl-tooltip
      ref="tooltipRef"
      :visible="dropMenuVisible"
      :placement="placement"
      :teleported="compatTeleported"
      :offset="popperOffset"
      :popper-class="`${namespace}-select-popper ${popperClass}`"
      :popper-options="popperOptions"
      :fallback-placements="['bottom-start', 'top-start', 'right', 'left']"
      :effect="effect"
      trigger="click"
      transition="dropdown"
      :persistent="persistent"
      :show-arrow="false"
      :stop-popper-mouse-event="false"
      :gpu-acceleration="false"
      @show="handleMenuEnter"
    >
      <template #default>
        <div
          class="select-trigger"
          @mouseenter="inputHovering = true"
          @mouseleave="inputHovering = false"
        >
          <hl-input
            :id="id"
            ref="reference"
            v-model="selectedLabel"
            native-type="text"
            :placeholder="
              typeof currentPlaceholder === 'function'
                ? currentPlaceholder()
                : currentPlaceholder
            "
            :name="name"
            :autocomplete="autocomplete"
            :size="selectSize"
            :fill="selectFill"
            :thin="thin"
            :round="round"
            :disabled="selectDisabled"
            :readonly="readonly"
            :validate-event="false"
            :label="ariaLabel"
            aria-autocomplete="none"
            aria-haspopup="listbox"
            :class="{
              'is-focus': visible,
              'is-tags': selected.length,
              'is-remote': !showIcon,
            }"
            :tabindex="(multiple && filterable) ? '-1' : null"
            role="combobox"
            :aria-activedescendant="hoverOption?.id || ''"
            :aria-controls="contentId"
            :aria-expanded="dropMenuVisible"
            @focus="handleFocus"
            @blur="handleBlur"
            @input="debouncedOnInputChange"
            @paste="debouncedOnInputChange"
            @compositionstart="handleComposition"
            @compositionupdate="handleComposition"
            @compositionend="handleComposition"
            @keydown.down.stop.prevent="navigateOptions('next')"
            @keydown.up.stop.prevent="navigateOptions('prev')"
            @keydown.enter.stop.prevent="selectOption"
            @keydown.esc="handleKeydownEscape"
            @keydown.tab="visible = false"
          >
            <template v-if="multiple" #tags>
              <div
                v-show="selected.length"
                ref="tags"
                tabindex="-1"
                :class="['input-tags', selectDisabled ? 'is-disabled' : '']"
                @click="focus"
              >
                <template v-if="collapseTags && selected.length">
                  <hl-tag
                    v-for="item in showTagList"
                    :key="getValueKey(item)"
                    :closable="!selectDisabled && !item.isDisabled"
                    :active="item.hitState"
                    disable-transitions
                    :size="selectSize"
                    :type="tagType"
                    @close="deleteTag($event, item)"
                  >
                    {{ item.currentLabel }}
                  </hl-tag>
                  <hl-tag
                    v-if="selected.length > maxCollapseTags"
                    :closable="false"
                    disable-transitions
                    :size="selectSize"
                    :type="tagType"
                  >
                    <hl-tooltip
                      v-if="collapseTagsTooltip"
                      ref="tagTooltipRef"
                      :disabled="dropMenuVisible"
                      :fallback-placements="['bottom', 'top', 'right', 'left']"
                      :effect="effect"
                      placement="bottom"
                      :teleported="false"
                      :popper-class="`collapse-tags-popper`"
                    >
                      <template #default>
                        + {{ selected.length - maxCollapseTags }}
                      </template>
                      <template #content>
                        <div class="collapse-tags">
                          <div
                            v-for="item in collapseTagList"
                            :key="getValueKey(item)"
                            class="collapse-tag"
                          >
                            <hl-tag
                              :key="getValueKey(item)"
                              class="in-tooltip"
                              :closable="!selectDisabled && !item.isDisabled"
                              :size="selectSize"
                              :hit="item.hitState"
                              :type="tagType"
                              disable-transitions
                              @close="handleDeleteTooltipTag($event, item)"
                            >
                              {{ item.currentLabel }}
                            </hl-tag>
                          </div>
                        </div>
                      </template>
                    </hl-tooltip>
                    <template v-else>
                      + {{ selected.length - maxCollapseTags }}
                    </template>
                  </hl-tag>
                </template>

                <template v-if="!collapseTags">
                  <slot name="tag" :items="selected">
                    <hl-tag
                      v-for="item in selected"
                      :key="getValueKey(item)"
                      :closable="!selectDisabled && !item.isDisabled"
                      :active="item.hitState"
                      disable-transitions
                      :size="selectSize"
                      :type="tagType"
                      @close="deleteTag($event, item)"
                    >
                      {{ item.currentLabel }}
                    </hl-tag>
                  </slot>
                </template>

                <input
                  v-if="filterable"
                  ref="input"
                  v-model="query"
                  type="text"
                  :class="[
                    'select-input',
                    selectSize ? `is-${ selectSize }` : '',
                    selectDisabled ? 'is-disabled' : ''
                  ]"
                  :aria-label="ariaLabel"
                  :disabled="selectDisabled"
                  :autocomplete="autocomplete"
                  @focus="handleFocus"
                  @blur="handleBlur"
                  @keyup="managePlaceholder"
                  @keydown="resetInputState"
                  @keydown.down.prevent="navigateOptions('next')"
                  @keydown.up.prevent="navigateOptions('prev')"
                  @keydown.esc="handleKeydownEscape"
                  @keydown.enter.stop.prevent="selectOption"
                  @keydown.delete="deletePrevTag"
                  @keydown.tab="visible = false"
                  @compositionstart="handleComposition"
                  @compositionupdate="handleComposition"
                  @compositionend="handleComposition"
                  @input="debouncedQueryChange"
                >
              </div>
            </template>
            <template v-if="$slots.prefix" #prefix>
              <div>
                <slot name="prefix"></slot>
              </div>
            </template>
            <template #suffix>
              <slot name="suffix" :className="`${iconClass} ${!showClose && showIcon ? 'visible' : ''}`">
                <hl-icon :class="[iconClass, !showClose && showIcon ? 'visible' : '']">
                  <system-select-arrow />
                </hl-icon>
              </slot>
              <slot name="clear">
                <hl-icon :class="['select-close', showClose && 'visible']" @click="handleClearClick">
                  <system-close />
                </hl-icon>
              </slot>
            </template>
          </hl-input>
        </div>
      </template>
      <template #content>
        <hl-select-menu>
          <template v-if="$slots.header" #header>
            <slot name="header"></slot>
          </template>
          <hl-scrollbar
            v-show="options.size > 0 && !loading"
            :id="contentId"
            ref="scrollbar"
            tag="ul"
            wrap-class="select-dropdown-wrap"
            view-class="select-dropdown-list"
            :class="{ 'is-empty': !allowCreate && Boolean(query) && filteredOptionsCount === 0 }"
            role="listbox"
            :aria-label="ariaLabel"
            aria-orientation="vertical"
            @at-end="$emit('at-end')"
          >
            <hl-option
              v-if="showNewOption"
              :value="query"
              :created="true"
            />
            <hl-options @update-options="onOptionsRendered">
              <slot></slot>
            </hl-options>
          </hl-scrollbar>
          <template v-if="emptyText && (!allowCreate || loading || (allowCreate && options.size === 0 ))">
            <slot v-if="$slots.empty" name="empty"></slot>
            <p v-else class="select-dropdown-empty">
              {{ emptyText }}
            </p>
          </template>
          <template v-if="$slots.footer" #footer>
            <slot name="footer"></slot>
          </template>
        </hl-select-menu>
      </template>
    </hl-tooltip>
  </div>
</template>

<script lang="ts">
import {
  toRefs,
  defineComponent,
  onMounted,
  nextTick,
  reactive,
  provide,
  computed,
} from 'vue'
import HlInput from '@hongluan-ui/components/input'
import { placements } from '@popperjs/core'
import { useResizeObserver } from '@vueuse/core'
import HlTag from '@hongluan-ui/components/tag'
import HlTooltip, { useTooltipContentProps } from '@hongluan-ui/components/tooltip'
import HlScrollbar from '@hongluan-ui/components/scrollbar'
import HlIcon from '@hongluan-ui/components/icon'
import { SystemSelectArrow, SystemClose } from '@hongluan-ui/components/system-icon'
import { useDeprecateAppendToBody } from '@hongluan-ui/components/popper'
import { ClickOutside } from '@hongluan-ui/directives'
import { isValidComponentSize } from '@hongluan-ui/utils'
import { useEmptyValuesProps, useId, useLocale, useNamespace, useAriaProps } from '@hongluan-ui/hooks'
import { UPDATE_MODEL_EVENT, CHANGE_EVENT } from '@hongluan-ui/constants'
import { useSelect, useSelectStates } from './useSelect'
import { selectKey } from './token'
import HlOption from './option.vue'
import HlOptions from './options'
import HlSelectMenu from './select-dropdown.vue'

import type { PropType } from 'vue'
import type { ComponentSize } from '@hongluan-ui/constants'
import type { Options } from '@popperjs/core'
import type { SelectContext } from './token'

export default defineComponent({
  name: 'Select',
  componentName: 'Select',
  components: {
    HlInput,
    HlSelectMenu,
    HlOption,
    HlOptions,
    HlTag,
    HlScrollbar,
    HlTooltip,
    HlIcon,
    SystemSelectArrow,
    SystemClose,
  },
  directives: { ClickOutside },
  props: {
    /**
     * @description the name attribute of select input
     */
    name: String,
    /**
     * @description native input id
     */
    id: String,
    /**
     * @description binding value
     */
    modelValue: {
      type: [Array, String, Number, Boolean, Object],
      default: undefined,
    },
    /**
     * @description the autocomplete attribute of select input
     */
    autocomplete: {
      type: String,
      default: 'off',
    },
    /**
     * @description for non-filterable Select, this prop decides if the option menu pops up when the input is focused
     */
    automaticDropdown: Boolean,
    /**
     * @description size of Input
     */
    size: {
      type: String as PropType<ComponentSize>,
      validator: isValidComponentSize,
    },
    /**
     * @description tooltip theme, built-in theme: `dark` / `light`
     */
    effect: {
      type: String as PropType<'light' | 'dark' | string>,
      default: '',
    },
    /**
     * @description whether Select is disabled
     */
    disabled: Boolean,
    /**
     * @description whether select can be cleared
     */
    clearable: Boolean,
    /**
     * @description whether Select is filterable
     */
    filterable: Boolean,
    /**
     * @description whether creating new items is allowed. To use this, `filterable` must be true
     */
    allowCreate: Boolean,
    block: Boolean,
    /**
     * @description whether Select is loading data from server
     */
    loading: Boolean,
    /**
     * @description custom class name for Select's dropdown
     */
    popperClass: {
      type: String,
      default: '',
    },
    /**
     * @description [popper.js](https://popper.js.org/docs/v2/) parameters
     */
    popperOptions: {
      type: Object as PropType<Partial<Options>>,
      default: () => ({} as Partial<Options>),
    },
    /**
     * @description whether options are loaded from server
     */
    remote: Boolean,
    /**
     * @description displayed text while loading data from server, default is 'Loading'
     */
    loadingText: String,
    /**
     * @description displayed text when no data matches the filtering query, you can also use slot `empty`, default is 'No matching data'
     */
    noMatchText: String,
    /**
     * @description displayed text when there is no options, you can also use slot `empty`, default is 'No data'
     */
    noDataText: String,
    /**
     * @description custom remote search method
     */
    remoteMethod: Function,
    /**
     * @description custom filter method
     */
    filterMethod: Function,
    /**
     * @description whether multiple-select is activated
     */
    multiple: Boolean,
    /**
     * @description maximum number of options user can select when `multiple` is `true`. No limit when set to 0
     */
    multipleLimit: {
      type: Number,
      default: 0,
    },
    /**
     * @description placeholder, default is 'Select'
     */
    placeholder: {
      type: String,
    },
    /**
     * @description select first matching option on enter key. Use with `filterable` or `remote`
     */
    defaultFirstOption: Boolean,
    /**
     * @description when `multiple` and `filter` is true, whether to reserve current keyword after selecting an option
     */
    reserveKeyword: {
      type: Boolean,
      default: true,
    },
    /**
     * @description unique identity key name for value, required when value is an object
     */
    valueKey: {
      type: String,
      default: 'value',
    },
    /**
     * @description whether to collapse tags to a text when multiple selecting
     */
    collapseTags: Boolean,
    /**
     * @description whether show all selected tags when mouse hover text of collapse-tags. To use this, `collapse-tags` must be true
     */
    collapseTagsTooltip: Boolean,
    /**
     * @description the max tags number to be shown. To use this, `collapse-tags` must be true
     */
    maxCollapseTags: {
      type: Number,
      default: 1,
    },
    popperAppendToBody: {
      type: Boolean,
      default: undefined,
    },
    /**
     * @description whether select dropdown is teleported to the body
     */
    teleported: useTooltipContentProps.teleported,
    /**
     * @description when select dropdown is inactive and `persistent` is `false`, select dropdown will be destroyed
     */
    persistent: {
      type: Boolean,
      default: true,
    },
    fill: Boolean,
    thin: Boolean,
    round: Boolean,
    /**
     * @description tag type
     */
    tagType: {
      type: String,
      default: '',
    },
    popperOffset: {
      type: Number,
      default: 4,
    },
    /**
     * @description whether to trigger form validation
     */
    validateEvent: {
      type: Boolean,
      default: true,
    },
    /**
     * @description in remote search method show suffix icon
     */
    remoteShowSuffix: Boolean,
    /**
     * @deprecated will be removed in version 2.4.0, please use override style scheme
     */
    suffixTransition: {
      type: Boolean,
      default: true,
    },
    /**
     * @description position of dropdown
     */
    placement: {
      type: String,
      values: placements,
      default: 'bottom-start',
    },
    ...useAriaProps(['ariaLabel']),
    ...useEmptyValuesProps,
  },
  emits: [UPDATE_MODEL_EVENT, CHANGE_EVENT, 'remove-tag', 'clear', 'visible-change', 'focus', 'blur'],

  setup(props, ctx) {
    const { t } = useLocale()
    const { namespace } = useNamespace()
    const states = useSelectStates(props)
    const contentId = useId()

    const {
      optionList,
      optionsArray,
      hoverOption,
      selectSize,
      readonly,
      handleResize,
      debouncedOnInputChange,
      debouncedQueryChange,
      deletePrevTag,
      deleteTag,
      deleteSelected,
      handleOptionSelect,
      scrollToOption,
      setSelected,
      // resetInputHeight,
      managePlaceholder,
      showClose,
      selectDisabled,
      selectFill,
      iconClass,
      showIcon,
      showNewOption,
      emptyText,
      toggleLastOptionHitState,
      resetInputState,
      handleComposition,
      onOptionCreate,
      onOptionDestroy,
      handleMenuEnter,
      handleFocus,
      blur,
      focus,
      handleBlur,
      handleClearClick,
      handleClose,
      handleKeydownEscape,
      toggleMenu,
      selectOption,
      getValueKey,
      navigateOptions,
      handleDeleteTooltipTag,
      dropMenuVisible,
      queryChange,
      groupQueryChange,
      reference,
      input,
      tooltipRef,
      tagTooltipRef,
      tags,
      selectWrapper,
      scrollbar,
      handleMouseEnter,
      handleMouseLeave,
      showTagList,
      collapseTagList,
    } = useSelect(props, states, ctx)

    const {
      // inputWidth,
      selected,
      // inputLength,
      filteredOptionsCount,
      visible,
      selectedLabel,
      hoverIndex,
      query,
      inputHovering,
      currentPlaceholder,
      menuVisibleOnFocus,
      isOnComposition,
      options,
      cachedOptions,
      optionsCount,
      // prefixWidth,
      // tagInMultiLine,
    } = toRefs(states)

    provide(selectKey, reactive({
      props,
      options,
      optionsArray,
      cachedOptions,
      optionsCount,
      filteredOptionsCount,
      hoverIndex,
      handleOptionSelect,
      onOptionCreate,
      onOptionDestroy,
      selectWrapper,
      selected,
      setSelected,
      queryChange,
      groupQueryChange,
    }) as unknown as SelectContext)

    onMounted(() => {
      states.cachedPlaceHolder = currentPlaceholder.value = props.placeholder || (() => t('hl.select.placeholder'))

      if (props.multiple && Array.isArray(props.modelValue) && props.modelValue.length > 0) {
        currentPlaceholder.value = ''
      }
      useResizeObserver(selectWrapper, handleResize)

      if (reference.value?.$el) {
        // const sizeMap = {
        //   md: 36,
        //   sm: 32,
        //   xs: 28,
        // }
        // const input = reference.value.input
        // states.initialInputHeight = input.getBoundingClientRect().height || sizeMap[selectSize.value]
      }
      if (props.remote && props.multiple) {
        // resetInputHeight()
      }
      nextTick(() => {
        if (reference.value?.$el) {
          // inputWidth.value = reference.value.$el.getBoundingClientRect().width
        }
        if (ctx.slots.prefix) {
          // const inputChildNodes = reference.value.$el.childNodes
          // const input = [].filter.call(inputChildNodes, item => item.tagName === 'INPUT')[0]
          // const prefix = reference.value.$el.querySelector('.input-affixe.prefix div')
          // const maxWidth = props.size === 'lg' ? 40 : 35
          // prefixWidth.value = Math.max(prefix.getBoundingClientRect().width + 5, maxWidth)
          // if (states.prefixWidth) {
          //   input.style.paddingLeft = `${Math.max(states.prefixWidth, maxWidth)}px`
          // }
        }
      })
      setSelected()
    })

    if (props.multiple && !Array.isArray(props.modelValue)) {
      ctx.emit(UPDATE_MODEL_EVENT, [])
    }
    if (!props.multiple && Array.isArray(props.modelValue)) {
      ctx.emit(UPDATE_MODEL_EVENT, '')
    }

    const popperPaneRef = computed(() => {
      return tooltipRef.value?.popperRef?.contentRef
    })

    const { compatTeleported } = useDeprecateAppendToBody(
      'Select',
      'popperAppendToBody',
    )

    const onOptionsRendered = (v: string[]) => {
      optionList.value = v
    }

    return {
      namespace,
      onOptionsRendered,
      // tagInMultiLine,
      // prefixWidth,
      compatTeleported,
      selectSize,
      readonly,
      handleResize,
      debouncedOnInputChange,
      debouncedQueryChange,
      deletePrevTag,
      deleteTag,
      handleDeleteTooltipTag,
      deleteSelected,
      handleOptionSelect,
      scrollToOption,
      // inputWidth,
      selected,
      // inputLength,
      filteredOptionsCount,
      visible,
      selectedLabel,
      hoverIndex,
      query,
      inputHovering,
      currentPlaceholder,
      menuVisibleOnFocus,
      isOnComposition,
      options,
      // resetInputHeight,
      managePlaceholder,
      showClose,
      selectDisabled,
      selectFill,
      iconClass,
      showIcon,
      showNewOption,
      emptyText,
      toggleLastOptionHitState,
      resetInputState,
      handleComposition,
      handleMenuEnter,
      handleFocus,
      handleKeydownEscape,
      blur,
      handleBlur,
      handleClearClick,
      handleClose,
      toggleMenu,
      selectOption,
      getValueKey,
      navigateOptions,
      dropMenuVisible,
      focus,
      reference,
      input,
      tooltipRef,
      popperPaneRef,
      tags,
      selectWrapper,
      scrollbar,
      handleMouseEnter,
      handleMouseLeave,
      showTagList,
      collapseTagList,
      tagTooltipRef,
      contentId,
      hoverOption,
    }
  },
})
</script>
